import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-bundles";
const TITLE = "Shopify Bundles erstellen: mit oder ohne Bundle App";
const DESCRIPTION =
  "Shopify Bundles und Sets verkaufen: kostenlose Bundle-App, Drittanbieter-Apps oder Umsetzung direkt im Theme. Vor- und Nachteile, Lagerlogik und Wirkung auf den Warenkorb.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Shopify Bundles lassen sich auf drei Wegen umsetzen: mit der kostenlosen Shopify-Bundles-App für einfache Sets, mit Drittanbieter-Apps für Mix-and-Match oder Mengenrabatte, oder direkt im Theme, wenn es um die Darstellung und den Verkaufsimpuls auf der Produktseite geht. Entscheidend ist weniger die App als die Frage, ob das Bundle den durchschnittlichen Warenkorb messbar erhöht.";

const faqs = [
  {
    q: "Wie erstelle ich Bundles in Shopify?",
    a: "Der einfachste Weg ist die kostenlose App Shopify Bundles: Sie wählen Produkte, legen einen Bundle-Preis fest, und der Bestand wird aus den Einzelprodukten berechnet. Für Mix-and-Match oder komplexe Rabattlogik braucht es Drittanbieter-Apps oder Entwicklung.",
  },
  {
    q: "Welche Bundle-App ist die richtige?",
    a: "Für feste Sets reicht die kostenlose Shopify-App. Für frei kombinierbare Bundles mit eigener Oberfläche kommen Apps wie Bundler oder ähnliche infrage. Prüfen Sie vor der Installation, welche Skripte die App lädt und wie sie sich auf die Ladezeit auswirkt.",
  },
  {
    q: "Erhöhen Bundles wirklich den Umsatz?",
    a: "Bundles wirken auf den durchschnittlichen Bestellwert, wenn sie einen echten Vorteil bieten: passende Kombination, kleiner Preisvorteil, geringere Auswahlermüdung. Ob es bei Ihnen trägt, zeigt nur ein Vergleich der Warenkorbhöhe vor und nach Einführung.",
  },
  {
    q: "Wie funktioniert der Lagerbestand bei Bundles?",
    a: "Bei der Shopify-Bundles-App und den meisten Drittanbieter-Apps wird der verfügbare Bestand aus den enthaltenen Einzelprodukten berechnet. Bei eigener Lagerlogik oder angebundener Warenwirtschaft ist das vorab zu prüfen.",
  },
  {
    q: "Kann ich Bundle-Optik im Theme anpassen lassen?",
    a: "Ja. Die Darstellung der Bundle-Kachel, Preisvergleiche und Hinweise auf der Produktseite lassen sich direkt im Theme bauen. Das ist oft der Teil, der über den Erfolg entscheidet, und kostet als Theme-Anpassung 110 € netto pro Stunde.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-bundles")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Conversion",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Conversion"
      title="Shopify Bundles: Sets, die den Warenkorb erhöhen"
      intro="Bundles sind einer der direktesten Hebel auf den durchschnittlichen Bestellwert, wenn sie richtig gebaut sind. Hier steht, welche Umsetzungswege Shopify bietet und woran Bundle-Angebote in der Praxis scheitern."
      source="ratgeber-shopify-bundles"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Drei Wege zum Bundle",
          p: [
            "Die kostenlose App Shopify Bundles deckt feste Sets ab: Produkte wählen, Bundle-Preis setzen, fertig. Der Bestand rechnet sich aus den Einzelprodukten, die Pflege bleibt im gewohnten Admin.",
            "Drittanbieter-Apps ergänzen Mix-and-Match-Oberflächen, Mengenrabatte und Bundle-Builder. Sie kosten monatlich und laden eigene Skripte, was Sie vor der Installation auf Ladezeit prüfen sollten.",
            "Der dritte Weg ist die Umsetzung im Theme: Bundle-Logik als Variante oder eigene Section, volle Kontrolle über Darstellung und keine monatlichen App-Kosten. Sinnvoll, wenn die Bundle-Struktur überschaubar ist.",
          ],
        },
        {
          h: "Was ein Bundle verkaufen muss",
          p: [
            "Ein Bundle funktioniert, wenn der Vorteil sofort sichtbar ist. Das scheitert selten an der Technik und häufig an der Präsentation.",
          ],
          list: [
            "Der Preisvorteil steht als konkrete Zahl am Angebot, nicht im Kleingedruckten",
            "Die Kombination ergibt Sinn: Produkte, die Kunden ohnehin zusammen kaufen",
            "Die Bundle-Kachel erscheint dort, wo die Kaufentscheidung fällt, nicht drei Scrolls zu spät",
            "Verfügbarkeit und Lieferzeit des Sets sind so klar wie beim Einzelprodukt",
          ],
        },
        {
          h: "Bundle und Conversion messen",
          p: [
            "Ob Bundles tragen, entscheidet der Vergleich: durchschnittlicher Bestellwert und Anteil der Bundle-Käufe vor und nach Einführung. Vier Wochen Beobachtung reichen für eine erste belastbare Richtung.",
            "Wer es genauer wissen will, testet zwei Bundle-Varianten gegeneinander: Preisvorteil in Prozent gegenüber Festbetrag, Kachel auf der Produktseite gegenüber eigenem Bundle-Bereich.",
          ],
        },
        {
          h: "Sonderfälle: Lager, Warenwirtschaft, Retouren",
          p: [
            "Sobald eine Warenwirtschaft den Bestand führt, muss sie Bundles verstehen können, sonst verkaufen Sie Sets, die nicht lieferbar sind. Billbee, JTL und vergleichbare Systeme behandeln Stücklisten unterschiedlich, das gehört vor dem Livegang geprüft.",
            "Auch Retouren wollen geklärt sein: Wird das ganze Set retourniert oder das Einzelteil, und wie bildet Ihr System das ab.",
          ],
        },
        {
          h: "Was ich dabei übernehme",
          p: [
            "Ich baue Bundle-Darstellung und -Logik direkt im Theme, prüfe App-Optionen auf Ladezeit und Kosten und messe die Wirkung auf den Bestellwert. Theme-Arbeit zu 110 € netto pro Stunde, Conversion-Projekte ab 3.990 € netto zum Festpreis.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung und Entwicklung" },
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung ab 3.990 €" },
        { to: "/blog/shopify-apps-auswaehlen", label: "Shopify Apps richtig auswählen" },
        { to: "/roi-rechner", label: "Umsatzeffekt berechnen" },
        { to: "/kontakt", label: "Bundle-Projekt anfragen" },
      ]}
    />
  );
}
